import type {MutationCallbacks} from "../../../../types/callbacks";

import {useMutation, useQueryClient} from "@tanstack/react-query";

import {userKeys} from "../../../../user-keys";

import {removeTaskMutationOptions} from "./remove-task.mutation-options";

/**
 * Command hook: Remove a task from a user.
 *
 * DEFAULT BEHAVIOR (always applied):
 * - Invalidate the user detail query so the removed task disappears
 *
 * USER CALLBACKS (optional, applied after defaults):
 * - onSuccess: e.g., show success toast
 * - onError: e.g., show error toast
 *
 * @param callbacks - Optional callbacks for custom logic
 *
 * @example
 * const mutation = useRemoveTask({
 *   onSuccess: () => showToast("Task removed"),
 *   onError: (err) => showToast(`Error: ${err.message}`),
 * });
 *
 * mutation.mutate({ userId, taskId });
 */
export function useRemoveTask(
  callbacks?: Partial<MutationCallbacks<{userId: string; taskId: string}, void>>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...removeTaskMutationOptions(),

    onSuccess: (_, vars) => {
      // ─── DEFAULT: Invalidate user detail so removed task disappears ──
      void queryClient.invalidateQueries({queryKey: userKeys.detail(vars.userId)});

      // ─── USER: Custom onSuccess callback ──────────────────────────
      callbacks?.onSuccess?.();
    },

    onError: (err, vars, ctx) => {
      // ─── USER: Custom onError callback ───────────────────────────
      callbacks?.onError?.(err, vars, ctx);
    },
  });
}
